import * as React from "react";
import { FC, JSX, useMemo } from "react";
import { NextClosestImpactProviderTableData, NextClosestImpactProviderTableDataWithCargo } from "@app/projects/servicing-provider/types";
import { buildAdequacyProvidersColumnConfig } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import ProjectOptions from "@app/projects/projectOptions";
import { SortDirections, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { TableType, ViewableField } from "@app/viewable-fields/viewableFields";

type DisplayedProviderType = "excluded" | "nonExcluded";

interface NextClosestImpactProviderTableProps {
  bulkActionBar: JSX.Element;
  data: (NextClosestImpactProviderTableData | NextClosestImpactProviderTableDataWithCargo)[];
  displayedProviderType: DisplayedProviderType;
  providerType: ProviderNetworkTypeEnum;
  handleSort: (col: keyof NextClosestImpactProviderTableDataWithCargo, direction: SortDirections) => void;
  loading: boolean;
  project: ProjectOptions;
  runConfiguration: ProjectRunConfiguration;
  onSelectedProvidersChanged: React.Dispatch<React.SetStateAction<string[]>>
  selectedProviders: string[];
  setSkip: (newSkip: number) => void;
  showDist: boolean;
  showTime: boolean;
  skip: number;
  tableType: TableType;
  take: number;
  totalRecords: number;
  viewableFields: ViewableField[];
  showProviderValidations?: boolean;
  showSpecialtyPredictionModel?: boolean;
  showPgrConfidenceTier?: boolean;
}

export const NextClosestImpactProviderTable: FC<React.PropsWithChildren<NextClosestImpactProviderTableProps>> = (props: NextClosestImpactProviderTableProps) => {
  const columnConfig = useMemo(() => {
    const isExclusion = props.displayedProviderType === "excluded";
    return buildAdequacyProvidersColumnConfig(
      props.viewableFields,
      props.tableType,
      props.project.showNppesDates && props.runConfiguration && props.runConfiguration.showNppesDates,
      props.project.showMipsScore && props.runConfiguration && props.runConfiguration.showMipsScore,
      props.project.showPciData && props.runConfiguration && props.runConfiguration.showPciData,
      props.project.showVaClaimsVolumeColumn,
      props.project.showTricareClaimsVolumeColumn,
      props.project.showAverageDistance && props.showDist,
      props.project.showAverageTime && props.showTime,
      isExclusion,
      false,
      true,
      isExclusion,
      props.project.showLocationConfidenceScores && props.runConfiguration?.showLocationConfidenceScores,
      props.project.showMotiveAppropriatenessScore && props.runConfiguration?.showMotiveAppropriatenessScore,
      props.runConfiguration?.showCareJourneyScores,
      undefined, //showProviderAffiliations
      undefined, //affiliationsDisplayOnNetworkAccordion
      undefined, //showProviderGrouping
      props.showProviderValidations, //showProviderValidations
      undefined, //isZipProject
      props.showSpecialtyPredictionModel,
      props.showPgrConfidenceTier,
    );

  }, [props.viewableFields, props.tableType, props.project, props.runConfiguration, props.displayedProviderType, props.showDist, props.showTime, props.showPgrConfidenceTier]);

  const paginationProps: WafflesPaginationProps = {
    changePage: (newPageNumber) => { props.setSkip((newPageNumber - 1) * props.take) },
    recordsPerPage: props.take,
    skip: props.skip,
    totalRecords: props.totalRecords,
  };

  return (
    <Waffles<NextClosestImpactProviderTableData | NextClosestImpactProviderTableDataWithCargo>
      bulkActionBar={props.bulkActionBar}
      columns={columnConfig}
      customSort={(segment, col, direction) => { props.handleSort(col, direction); return segment; }}
      data={props.data}
      enablePagination={true}
      fullWidth={true}
      handleCheckboxChange={props.onSelectedProvidersChanged}
      loading={props.loading}
      paginationProps={paginationProps}
      passThroughSorting={true}
      selectedKeys={props.selectedProviders}
      showCheckboxes={true}
      testId={`next-closest-${props.providerType}-provider-table`}
    />
  );
}